import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { AttendanceApi } from '@/services/school.api';
import { AcademicYearsApi } from '@/services/academic.api';

/**
 * Shared "which year should this screen open on" logic for every attendance
 * reporting screen. Deliberately NOT `AcademicYearsApi.current`/`is_current`
 * — migrated attendance sits mostly in 2024-25/2025-26, and the school's
 * `is_current` year (2026-27) has almost no data, so defaulting to it would
 * open every screen empty. `AttendanceApi.defaultYear()` instead returns the
 * most recent year that actually HAS attendance rows for this school. The
 * year selector still lists every year (`AcademicYearsApi.list`, the
 * migrated-shape one from academic.api.ts — NOT school.api.ts's old
 * `AcademicYearsApi`, which hits the incompatible `/school/academic-years`
 * and is what the pre-existing Mark Attendance page still uses) so older
 * years stay reachable.
 */
export function useDefaultYear() {
  const [yearId, setYearId] = useState('');

  const { data: defaultYear, isLoading: defaultYearLoading } = useQuery({
    queryKey: ['attendance', 'default-year'],
    queryFn: AttendanceApi.defaultYear,
  });

  const { data: yearsPage } = useQuery({
    queryKey: ['academic-years', 'all-for-attendance-selector'],
    queryFn: () => AcademicYearsApi.list({ limit: 100, sortBy: 'start_date', sortOrder: 'desc' }),
  });
  const years = yearsPage?.items ?? [];

  const effectiveYearId = yearId || defaultYear?.id || '';
  const selectedYear =
    years.find((y) => y.id === effectiveYearId) ??
    (defaultYear && defaultYear.id === effectiveYearId ? defaultYear : null);

  return {
    years,
    yearId,
    setYearId,
    effectiveYearId,
    selectedYear,
    defaultYear,
    isLoading: defaultYearLoading,
    hasAnyData: !!defaultYear,
  };
}
